import '@/assets/css/shepherd-custom.css';
import { useEventBus } from '@/composables/useEventBus';
import { useFetch } from '@/composables/useFetch';
import { buildAllSteps } from '@/onboarding/stepBuilder';
import { removeLottie } from '@/onboarding/stepStyles';
import { useDishesStore } from '@/stores/dishesStore';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore'; // Import du store User
import { offset } from '@floating-ui/dom';
import type { StepOptions } from 'shepherd.js';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';
import { computed, nextTick, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, type Router } from 'vue-router';

// --- TYPES ---
export type OnboardingStepType = 'info' | 'action' | 'waiting' | 'success';

// --- GLOBAL STATE pour la navigation ---
let globalOnboardingInstance: ReturnType<typeof useOnboarding> | null = null;

export type OnboardingStepDeps = {
  tour: Ref<InstanceType<typeof Shepherd.Tour> | null>;
  router: Router;
  dishesStore: ReturnType<typeof useDishesStore>;
  eventBus: ReturnType<typeof useEventBus>;
  waitForElement: (selector: string, timeout?: number) => Promise<Element>;
  markChapterAsComplete: (chapterKey: string) => Promise<void>;
  showChapterNavigation: () => void;
  navigateToChapter: (chapterKey: string) => Promise<void>;
  skipToNextChapter: (currentChapterKey: string) => void;
  handleOnboardingError: (error: Error, context: string, chapterKey?: string) => void;
  currentRole: string;
};

// --- CONSTANTES ---
const LS_MANUAL_CLOSE_KEY = 'onboarding_manually_closed';

const allChapters = [
  '01_Introduction',
  '02_RecipeGeneration',
  '03_RecipeDetails',
  '04_RecipeActions',
  '05_Organization',
  '06_Scan',
  '07_ProfileAndTeam',
  '08_Planning',
  '09_End',
];

// Chapitres disponibles selon le rôle
const getChaptersForRole = (role: string): string[] => {
  switch (role) {
    case 'admin':
      return allChapters; // Tous les chapitres
    case 'single_user':
      return ['01_Introduction', '02_RecipeGeneration', '03_RecipeDetails', '04_RecipeActions', '05_Organization', '06_Scan', '09_End'];
    case 'employee':
      return ['01_Introduction', '08_Planning', '09_End'];
    default:
      return allChapters; // Par défaut, tous les chapitres
  }
};

// Chapitres affichables dans l'UI (exclut le chapitre de fin)
const getDisplayableChaptersForRole = (role: string): string[] => {
  const allRoleChapters = getChaptersForRole(role);
  return allRoleChapters.filter((chapter) => chapter !== '09_End');
};

// Mapping des vraies premières étapes de chaque chapitre (sans transitions)
const chapterFirstStepId: Record<string, string> = {
  '01_Introduction': 'welcome',
  '02_RecipeGeneration': 'create-recipe',
  '03_RecipeDetails': 'recipe-details',
  '04_RecipeActions': 'final-recipe-details',
  '05_Organization': 'recipes-list',
  '06_Scan': 'scan-intro',
  '07_ProfileAndTeam': 'page-profile-transition',
  '08_Planning': 'planning-link',
  '09_End': 'end',
};

// Mapping des étapes de transition par chapitre pour le retour
const chapterTransitionStepId: Record<string, string> = {
  '01_Introduction': 'welcome', // Pas de transition pour intro
  '02_RecipeGeneration': 'transition-1-2',
  '03_RecipeDetails': 'transition-2-3',
  '04_RecipeActions': 'transition-3-4',
  '05_Organization': 'transition-4-5',
  '06_Scan': 'transition-5-6',
  '07_ProfileAndTeam': 'transition-6-7',
  '08_Planning': 'transition-7-8',
  '09_End': 'transition-8-9',
};

const chapterStartStepId: Record<string, string> = {
  '01_Introduction': 'welcome',
  '02_RecipeGeneration': 'create-recipe', // Maintenant la première étape
  '03_RecipeDetails': 'recipe-details',
  '04_RecipeActions': 'final-recipe-details',
  '05_Organization': 'transition-to-recipes-list',
  '06_Scan': 'transition-to-scan',
  '07_ProfileAndTeam': 'page-profile-transition',
  '08_Planning': 'transition-to-planning',
  '09_End': 'end',
};

// --- STATE UI GLOBAL (Reste local car purement UI) ---
const isOnboardingActive: Ref<boolean> = ref(false);
const isLoadingStatus: Ref<boolean> = ref(false);
const isChapterNavigationVisible: Ref<boolean> = ref(false);
const savedStepIdForReturn: Ref<string | null> = ref(null);

// Helper style (inchangé)
export function createOnboardingStep(baseStep: StepOptions, stepType: OnboardingStepType = 'info'): StepOptions {
  const step = { ...baseStep };
  let classes = step.classes || '';
  switch (stepType) {
    case 'action':
      classes += ' shepherd-action-required';
      break;
    case 'waiting':
      classes += ' shepherd-waiting';
      break;
    case 'success':
      classes += ' shepherd-success';
      break;
  }
  step.classes = classes.trim();
  return step;
}

export function useOnboarding() {
  const eventBus = useEventBus();
  const tour: Ref<InstanceType<typeof Shepherd.Tour> | null> = ref(null);
  const router = useRouter();
  const dishesStore = useDishesStore();
  const userStore = useUserStore(); // SOURCE UNIQUE DE VÉRITÉ
  const { fetchData } = useFetch();
  const envStore = useEnvStore();
  const apiUrl = envStore.apiUrl;
  const { t } = useI18n();

  // --- REACTIVE STATE ---
  const shouldShowOnboardingDialog = ref(false);

  // --- COMPUTED STATE (Basé sur le Store) ---

  // Calcule les chapitres terminés directement depuis le userStore
  const completedChapters = computed(() => {
    if (!userStore.user?.onboarding_progress) return [];
    return Object.entries(userStore.user.onboarding_progress)
      .filter(([_, val]) => val.completed)
      .map(([key]) => key);
  });

  const currentRole = computed(() => userStore.contextualRole);

  // Chapitres disponibles selon le rôle actuel
  const chapterOrder = computed(() => getChaptersForRole(currentRole.value));

  // Synchronisé avec le store
  const isFullyComplete = computed(() => {
    return !!userStore.user?.onboarding_complete;
  });

  // --- HELPERS POUR LES RÔLES ---

  /**
   * Vérifie si un chapitre est disponible pour le rôle actuel
   */
  function isChapterAvailableForRole(chapterKey: string): boolean {
    return chapterOrder.value.includes(chapterKey);
  }

  // --- HELPERS LOCAL STORAGE (Centralisés) ---
  const isManuallyClosed = () => localStorage.getItem(LS_MANUAL_CLOSE_KEY) === 'true';
  const setManuallyClosed = () => localStorage.setItem(LS_MANUAL_CLOSE_KEY, 'true');
  const clearManuallyClosed = () => localStorage.removeItem(LS_MANUAL_CLOSE_KEY);

  // --- API METHODS ---

  /**
   * Récupère le statut et met à jour le UserStore
   */
  async function fetchOnboardingStatus() {
    if (isLoadingStatus.value) return;
    isLoadingStatus.value = true;

    try {
      const { data, error } = await fetchData(`${apiUrl}/onboarding/status`, 'GET');

      if (error) throw new Error('Erreur API Status');

      if (data && data.data && userStore.user) {
        // MISE À JOUR DU STORE (Source de vérité)
        const status = data.data;
        userStore.user.onboarding_complete = status.onboarding_complete;
        userStore.user.onboarding_progress = status.progress;

        console.log('[Onboarding] Store updated via API Status');
      }
    } catch (err) {
      console.error('[Onboarding] Fetch status error:', err);
    } finally {
      isLoadingStatus.value = false;
    }
  }

  /**
   * Sauvegarde la progression et met à jour le UserStore
   */
  async function saveChapterProgress(chapterKey: string) {
    try {
      const payload = {
        chapter: chapterKey,
        completed_at: new Date().toISOString(),
      };

      const { data, error } = await fetchData(`${apiUrl}/onboarding/progress`, 'POST', payload);

      if (error) throw error;

      // Mise à jour fiable via la réponse API
      if (data?.data?.user && userStore.user) {
        // On merge ou on remplace selon la structure de retour
        // Ici on suppose que l'API renvoie l'user à jour ou le progress à jour
        if (data.data.user.onboarding_progress) {
          userStore.user.onboarding_progress = data.data.user.onboarding_progress;
        }
      }
    } catch (err) {
      console.error(`[Onboarding] Error saving ${chapterKey}:`, err);
      // En cas d'erreur, on pourrait vouloir rollback l'état optimiste ici
    }
  }

  async function completeOnboardingAPI() {
    try {
      const { data: _data, error } = await fetchData(`${apiUrl}/onboarding/complete`, 'POST', {});
      if (error) throw error;

      if (userStore.user) {
        userStore.user.onboarding_complete = true;
      }

      // Nettoyer le localStorage des tags d'onboarding
      localStorage.removeItem('onboarding:tags');
      console.log('[Onboarding] localStorage des tags nettoyé');

      // Recharger les vrais tags depuis la DB
      const { useDishesStore } = await import('@/stores/dishesStore');
      const { useUserStore } = await import('@/stores/useUserStore');
      const user = useUserStore().user;
      if (user?.restaurants[0]?.id) {
        console.log('[Onboarding] Rechargement des vrais tags depuis la DB');
        // Note: on passe un toast minimal ici, l'utilisateur ne devrait pas voir d'erreurs
        const mockToast = { add: () => {} };
        await useDishesStore().fetchTags(user.restaurants[0].id, mockToast, false);
      }

      console.log('[Onboarding] Fully completed!');
    } catch (err) {
      console.error('[Onboarding] Error completing:', err);
    }
  }

  /**
   * Dismiss onboarding (user chose "do not show again")
   * This marks the onboarding as complete and dismissed on the backend
   */
  async function dismissOnboardingAPI() {
    try {
      const { data, error } = await fetchData(`${apiUrl}/onboarding/dismiss`, 'POST', {});
      if (error) throw error;

      if (userStore.user) {
        // userStore.user.onboarding_complete = true;
        userStore.user.onboarding_dismissed = true;
      }

      // Also clear any local storage keys related to onboarding prompts
      localStorage.removeItem('onboardingPromptDismissed');

      console.log('[Onboarding] Dismissed successfully!');
      return data;
    } catch (err) {
      console.error('[Onboarding] Error dismissing:', err);
      throw err;
    }
  }

  async function resetOnboardingAPI() {
    try {
      await fetchData(`${apiUrl}/onboarding/reset`, 'POST', {});

      // Reset du store
      if (userStore.user) {
        userStore.user.onboarding_complete = false;
        userStore.user.onboarding_progress = {};
      }

      clearManuallyClosed();
      console.log('[Onboarding] Reset successful');
    } catch (err) {
      console.error('[Onboarding] Reset error:', err);
    }
  }

  // --- LOGIQUE METIER ---

  async function markChapterAsComplete(chapterKey: string) {
    if (!userStore.user) return;

    // Vérification via le Store (Source de vérité)
    const isAlreadyDone = userStore.user.onboarding_progress?.[chapterKey]?.completed;

    if (!isAlreadyDone) {
      // 1. Mise à jour OPTIMISTE du Store
      if (!userStore.user.onboarding_progress) userStore.user.onboarding_progress = {};

      // On crée l'entrée si elle n'existe pas
      userStore.user.onboarding_progress[chapterKey] = {
        completed: true,
        completed_at: new Date().toISOString(),
      };

      // 2. Appel API
      await saveChapterProgress(chapterKey);
    }

    if (chapterKey === '09_End') {
      await completeOnboardingAPI();
    }
  }

  function getProgress(): string[] {
    return completedChapters.value; // Retourne la valeur computed du store
  }

  function getLastCompletedChapter(): string | null {
    const completed = getProgress();
    if (completed.length === 0) return null;

    const completedSorted = completed.slice().sort((a, b) => {
      return chapterOrder.value.indexOf(a) - chapterOrder.value.indexOf(b);
    });
    return completedSorted[completedSorted.length - 1];
  }

  function getNextStartStepId(): string {
    const lastCompleted = getLastCompletedChapter();
    if (!lastCompleted) {
      return chapterStartStepId['01_Introduction'];
    }

    const lastIndex = chapterOrder.value.indexOf(lastCompleted);
    if (lastIndex === -1 || lastIndex === chapterOrder.value.length - 1) {
      return isFullyComplete.value ? 'end' : chapterStartStepId['01_Introduction'];
    }

    const nextChapterKey = chapterOrder.value[lastIndex + 1];
    return chapterStartStepId[nextChapterKey] || 'welcome';
  }

  // --- LISTENERS (Utilisent les helpers centralisés) ---
  function setupEventListeners() {
    // Plus de localStorage direct ici
    eventBus.on('onboarding:recipe-list-ready', () => {
      if (isManuallyClosed()) return;
      if (tour.value?.getCurrentStep()?.id === 'wait-generation') tour.value.next();
    });
    eventBus.on('onboarding:recipe-summary-ready', () => {
      if (isManuallyClosed()) return;
      if (tour.value?.getCurrentStep()?.id === 'recipe-detail-loading') tour.value.next();
    });
    eventBus.on('onboarding:recipe-suggestion-clicked', () => {
      if (isManuallyClosed()) return;
      if (tour.value?.getCurrentStep()?.id === 'select-recipe') tour.value.next();
    });
    eventBus.on('onboarding:generated-images-ready', () => {
      if (isManuallyClosed()) return;
      if (tour.value?.getCurrentStep()?.id === 'wait-generated-images') tour.value.next();
    });
    eventBus.on('onboarding:ingredient-clicked', () => {
      if (isManuallyClosed()) return;
      if (tour.value?.getCurrentStep()?.id === 'edit-ingredients') tour.value.next();
    });
    eventBus.on('onboarding:history-menu-opened', () => {
      if (isManuallyClosed()) return;
      if (tour.value?.getCurrentStep()?.id === 'classify-recipe') tour.value.next();
    });
    eventBus.on('onboarding:choose-folder-dialog-opened', () => {
      if (isManuallyClosed()) return;
      if (tour.value?.getCurrentStep()?.id === 'menu-classify-dish') tour.value.next();
    });
    eventBus.on('onboarding:recipe-detail-loaded', () => {
      if (isManuallyClosed()) return;
      if (tour.value?.getCurrentStep()?.id === 'recipe-detail-loading') tour.value.next();
    });
    eventBus.on('onboarding:dialog-canceled-by-user', () => {
      if (!tour.value) return;
      const id = tour.value.getCurrentStep()?.id;
      if (['validate-folder-dialog', 'validate-subfolder-dialog', 'choose-folder'].includes(id || '')) {
        tour.value.back();
      }
    });
    eventBus.on('onboarding:dialog-canceled-by-user-during-dish-generation', () => {
      if (tour.value?.getCurrentStep()?.id === 'validate-recipe-selection') tour.value.back();
    });
    eventBus.on('onboarding:dialog-canceled-by-user-during-tags-selection', () => {
      if (tour.value?.getCurrentStep()?.id === 'choose-folder') tour.value.show('classify-recipe');
    });

    document.addEventListener(
      'focusin',
      (e) => {
        const target = e.target as HTMLElement;

        if (!target || !['INPUT', 'TEXTAREA'].includes(target.tagName)) return;

        if (tour.value && tour.value.isActive()) {
          setTimeout(() => {
            interface RefreshableTour {
              refresh?: () => void;
            }
            const refreshable = tour.value as unknown as RefreshableTour;
            refreshable.refresh?.();
          }, 400);
        }
      },
      true,
    );
  }

  // --- MAIN FUNCTIONS ---

  async function startTour(stepId?: string) {
    console.log('Starting onboarding tour...');

    await fetchOnboardingStatus();

    clearManuallyClosed();

    if (isFullyComplete.value && !stepId) {
      console.log('[Onboarding] Déjà terminé. (Replay possible via stepId)');
      // Optionnel: return;
    }

    if (!tour.value) {
      initTour();
    }

    isOnboardingActive.value = true;

    // Initialiser le mode onboarding pour les tags (effacer les tags DB et charger les tags locaux)
    const { useDishesStore } = await import('@/stores/dishesStore');
    useDishesStore().initOnboardingMode();

    if (stepId) {
      tour.value?.show(stepId);
    } else {
      const startStep = getNextStartStepId();
      console.log(`[Onboarding] Reprise à l'étape: ${startStep}`);
      tour.value?.show(startStep);
    }
  }

  function nextStepIfCurrent(id: string) {
    if (isManuallyClosed()) return;
    if (tour.value && tour.value.getCurrentStep()?.id === id) {
      tour.value.next();
    }
  }

  router.afterEach((to, from) => {
    if (isManuallyClosed()) return;
    if (tour.value && tour.value.getCurrentStep()?.id === 'select-generated-image' && from.path.includes('/images') && !to.path.includes('/images')) {
      tour.value.next();
    }
  });

  async function waitForElement(selector: string, timeout = 10000): Promise<Element> {
    return new Promise<Element>((resolve) => {
      const element = document.querySelector(selector);
      if (element) return resolve(element);

      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) {
          observer.disconnect();
          clearTimeout(timeoutId);
          resolve(element);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });

      const timeoutId = setTimeout(() => {
        observer.disconnect();
        const fallback = document.createElement('div');
        fallback.setAttribute('data-onboarding-fallback', 'true');
        resolve(fallback);
        handleOnboardingError(new Error(`Timeout waiting for ${selector}`), `waitForElement(${selector})`, getCurrentChapter() || undefined);
      }, timeout);
    });
  }

  // --- ERREURS ---
  function showCustomConfirmModal(title: string, message: string, onConfirm: () => void, onCancel?: () => void, isNotification = false) {
    // Créer l'overlay
    const overlay = document.createElement('div');
    overlay.id = 'onboarding-confirm-modal';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      z-index: 999999;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Créer le modal
    const modal = document.createElement('div');
    modal.style.cssText = `
      background: white;
      border-radius: 8px;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      font-family: 'Sora', sans-serif;
    `;

    const buttonsHtml = isNotification
      ? `<div style="display: flex; gap: 12px; justify-content: center;">
          <button id="confirm-btn" style="
            background: #4b79a1;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 8px 24px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            font-family: 'Sora', sans-serif;
          ">${t('onboarding.dialog.confirmation.continue')}</button>
        </div>`
      : `<div style="display: flex; gap: 12px; justify-content: flex-end;">
          <button id="cancel-btn" style="
            background: white;
            color: #6b7280;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            padding: 8px 16px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            font-family: 'Sora', sans-serif;
          ">${t('onboarding.dialog.confirmation.no')}</button>
          <button id="confirm-btn" style="
            background: #dc2626;
            color: white;
            border: none;
            border-radius: 6px;
            padding: 8px 16px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            font-family: 'Sora', sans-serif;
          ">${t('onboarding.dialog.confirmation.yes')}</button>
        </div>`;
    modal.innerHTML = `
      <div style="margin-bottom: 16px;">
        <h3 style="margin: 0 0 8px 0; color: #1f2937; font-size: 18px; font-weight: 600;">${title}</h3>
        <p style="margin: 0; color: #6b7280; font-size: 14px; line-height: 1.5; white-space: pre-line;">${message}</p>
      </div>
      ${buttonsHtml}
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Gérer les clics
    const confirmBtn = modal.querySelector('#confirm-btn') as HTMLButtonElement;
    const cancelBtn = modal.querySelector('#cancel-btn') as HTMLButtonElement;

    confirmBtn.addEventListener('click', () => {
      document.body.removeChild(overlay);
      onConfirm();
    });

    if (cancelBtn && onCancel) {
      cancelBtn.addEventListener('click', () => {
        document.body.removeChild(overlay);
        onCancel();
      });
    }

    // Fermer avec Escape
    const handleKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        document.body.removeChild(overlay);
        document.removeEventListener('keydown', handleKeydown);
        if (isNotification || !onCancel) {
          onConfirm();
        } else {
          onCancel();
        }
      }
    };
    document.addEventListener('keydown', handleKeydown);

    // Fermer en cliquant sur l'overlay
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        document.body.removeChild(overlay);
        if (isNotification || !onCancel) {
          onConfirm();
        } else {
          onCancel();
        }
      }
    });
  }

  function handleOnboardingError(error: Error, context: string, _chapterKey?: string) {
    console.error(`[Onboarding Error] ${context}`, error);

    setManuallyClosed();
    tour.value?.complete();
    isOnboardingActive.value = false;
    removeLottie();

    // Afficher un modal informatif et rediriger vers l'overview
    showErrorNotificationModal();
  }

  function showErrorNotificationModal() {
    showCustomConfirmModal(
      t('onboarding.dialog.confirmation.errorTitle'),
      t('onboarding.dialog.confirmation.errorMessage'),
      () => {
        // Rediriger vers l'overview de l'onboarding
        router.push('/profile/onboarding');
      },
      undefined,
      true, // Mode notification avec un seul bouton
    );
  }

  function skipToNextChapter(currentChapterKey: string) {
    const currentIndex = chapterOrder.value.indexOf(currentChapterKey);

    // Trouver le prochain chapitre disponible pour le rôle actuel
    let nextAvailableChapter: string | undefined;
    for (let i = currentIndex + 1; i < chapterOrder.value.length; i++) {
      const chapterKey = chapterOrder.value[i];
      if (isChapterAvailableForRole(chapterKey)) {
        nextAvailableChapter = chapterKey;
        break;
      }
    }

    // Marquer le chapitre actuel comme terminé
    markChapterAsComplete(currentChapterKey);

    if (nextAvailableChapter) {
      // Naviguer vers le prochain chapitre disponible
      navigateToChapter(nextAvailableChapter);
    } else {
      // Si pas de chapitre suivant disponible, aller au chapitre de fin s'il est disponible
      if (isChapterAvailableForRole('09_End')) {
        navigateToChapter('09_End');
      }
    }
  }

  // --- INITIALISATION ---

  function initTour() {
    setupEventListeners();

    tour.value = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'gastronomia-tour-step bg-white text-gray-800 border border-gray-300 shadow-md rounded-lg',
        scrollTo: { behavior: 'smooth', block: 'center' },
        cancelIcon: { enabled: true },
        arrow: true,
        modalOverlayOpeningPadding: 8,
        modalOverlayOpeningRadius: 4,
        floatingUIOptions: { middleware: [offset(15)] },
      },
      keyboardNavigation: false,
    });

    const deps: OnboardingStepDeps = {
      tour,
      router,
      dishesStore,
      eventBus,
      waitForElement,
      markChapterAsComplete,
      showChapterNavigation,
      navigateToChapter,
      skipToNextChapter,
      handleOnboardingError,
      currentRole: currentRole.value,
    };

    const allSteps = buildAllSteps(deps);
    allSteps.forEach((step) => tour.value?.addStep(step));

    tour.value.on('complete', () => {
      removeLottie();
      isOnboardingActive.value = false;
      markChapterAsComplete('09_End');
    });

    tour.value.on('cancel', () => {
      const currentStep = tour.value?.getCurrentStep();

      // 1. Si c'est une étape de transition technique (invisible), on ferme sans demander
      if (currentStep?.id?.startsWith('transition-')) {
        removeLottie();
        isOnboardingActive.value = false;
        setManuallyClosed();
        router.push('/');
        return;
      }

      // 2. Afficher une confirmation avant de quitter l'onboarding
      showCustomConfirmModal(
        t('onboarding.dialog.confirmation.quitTitle'),
        t('onboarding.dialog.confirmation.quitMessage'),
        () => {
          // L'utilisateur confirme qu'il veut quitter
          removeLottie();
          isOnboardingActive.value = false;
          setManuallyClosed();
          router.push('/');
        },
        () => {
          // L'utilisateur annule - ne rien faire, l'onboarding continue
          console.log('[Onboarding] Annulation de la sortie - continuation du tour');
        },
      );
    });
  }

  // Fonction pour retourner à la transition step d'un chapitre
  function returnToChapterTransition(chapterKey: string): void {
    const transitionStepId = chapterTransitionStepId[chapterKey];
    if (transitionStepId && tour.value) {
      console.log(`[Onboarding] Retour à la transition du chapitre ${chapterKey}: ${transitionStepId}`);
      tour.value.show(transitionStepId);
    }
  }

  // --- NAVIGATION ---
  function getCurrentChapter(): string | null {
    const stepId = tour.value?.getCurrentStep()?.id;
    if (!stepId) return null;

    for (const [key, val] of Object.entries(chapterStartStepId)) {
      if (stepId === val) return key;
    }

    if (stepId?.startsWith('transition-')) {
      const transitionMatch = stepId.match(/transition-(\d+)-(\d+)/);
      if (transitionMatch) {
        const fromChapter = parseInt(transitionMatch[1]);
        return chapterOrder.value[fromChapter - 1];
      }
    }

    const stepPatterns = {
      welcome: '01_Introduction',
      'create-recipe': '02_RecipeGeneration',
      'wait-generation': '02_RecipeGeneration',
      'recipe-details': '03_RecipeDetails',
      'recipe-detail-loading': '03_RecipeDetails',
      'final-recipe-details': '04_RecipeActions',
      'transition-to-recipes-list': '05_Organization',
      'transition-to-scan': '06_Scan',
      'page-profile-transition': '07_ProfileAndTeam',
      'transition-to-planning': '08_Planning',
      end: '09_End',
    };

    for (const [pattern, chapterKey] of Object.entries(stepPatterns)) {
      if (stepId?.includes(pattern)) return chapterKey;
    }

    return null;
  }

  async function navigateToChapter(chapterKey: string): Promise<void> {
    console.log(`[Onboarding] Tentative navigation vers chapitre: ${chapterKey}`);
    console.log(`[Onboarding] Rôle actuel: ${currentRole.value}`);
    console.log(`[Onboarding] Chapitres disponibles: ${chapterOrder.value.join(', ')}`);

    // Vérifier si le chapitre est disponible pour le rôle actuel
    if (!isChapterAvailableForRole(chapterKey)) {
      console.warn(`[Onboarding] Chapitre ${chapterKey} non disponible pour le rôle ${currentRole.value}`);
      return;
    }

    console.log(`[Onboarding] Chapitre ${chapterKey} disponible, continuons...`);

    // Nettoyer l'étape sauvegardée car on navigue vers un nouveau chapitre
    savedStepIdForReturn.value = null;

    // Utiliser la vraie première étape du chapitre (pas la transition)
    const stepId = chapterFirstStepId[chapterKey];
    console.log(`[Onboarding] Première étape du chapitre: ${stepId}`);
    if (!stepId) {
      console.warn(`[Onboarding] Aucune première étape trouvée pour le chapitre ${chapterKey}`);
      return;
    }

    if (!tour.value) {
      console.log(`[Onboarding] Initialisation du tour...`);
      initTour();
    }

    clearManuallyClosed();
    isOnboardingActive.value = true;
    console.log(`[Onboarding] Onboarding activé`);

    const chapterRoutes: Record<string, string> = {
      '01_Introduction': '/',
      '02_RecipeGeneration': '/',
      '03_RecipeDetails': '/menu/dishes/-4',
      '04_RecipeActions': '/menu/dishes/-4',
      '05_Organization': '/',
      '06_Scan': '/my-recipes/my-scans',
      '07_ProfileAndTeam': '/',
      '08_Planning': '/',
      '09_End': '/',
    };

    const targetRoute = chapterRoutes[chapterKey];
    console.log(`[Onboarding] Route cible pour ${chapterKey}: ${targetRoute}`);
    console.log(`[Onboarding] Route actuelle: ${router.currentRoute.value.fullPath}`);

    try {
      if (targetRoute && router.currentRoute.value.fullPath !== targetRoute) {
        console.log(`[Onboarding] Navigation vers: ${targetRoute}`);
        await router.push(targetRoute);
        await nextTick();
        await new Promise((resolve) => setTimeout(resolve, 500));
      } else {
        console.log(`[Onboarding] Déjà sur la bonne route, pas de navigation nécessaire`);
      }
      console.log(`[Onboarding] Affichage de l'étape: ${stepId}`);
      tour.value?.show(stepId);
    } catch (error) {
      console.error(`[Onboarding] Erreur lors de la navigation vers ${chapterKey}:`, error);
      handleOnboardingError(error as Error, `MapsToChapter(${chapterKey})`, chapterKey);
    }
  }

  function showChapterNavigation(): void {
    console.log('[useOnboarding] showChapterNavigation appelée - étape actuelle:', tour.value?.getCurrentStep()?.id);

    // Sauvegarder l'étape actuelle pour pouvoir y revenir
    savedStepIdForReturn.value = tour.value?.getCurrentStep()?.id || null;
    console.log('[useOnboarding] Étape sauvegardée pour retour:', savedStepIdForReturn.value);

    // Suspendre le tour actuel
    if (tour.value) {
      tour.value.cancel();
    }

    // Activer l'affichage du modal de navigation des chapitres
    isChapterNavigationVisible.value = true;
    console.log('[useOnboarding] isChapterNavigationVisible mis à true');
  }

  function hideChapterNavigation(): void {
    isChapterNavigationVisible.value = false;
  }

  function handleChapterNavigationClose(): void {
    console.log('[useOnboarding] handleChapterNavigationClose appelée, savedStepIdForReturn:', savedStepIdForReturn.value);
    hideChapterNavigation();

    // Si on a une étape sauvegardée, y retourner (cas des transitions)
    if (savedStepIdForReturn.value) {
      console.log("[useOnboarding] Retour à l'étape sauvegardée:", savedStepIdForReturn.value);
      const stepToReturn = savedStepIdForReturn.value;
      savedStepIdForReturn.value = null; // Reset avant de relancer

      // Petit délai pour laisser le temps au DOM de se nettoyer
      setTimeout(() => {
        console.log("[useOnboarding] Relancement du tour avec l'étape:", stepToReturn);
        startTour(stepToReturn);
      }, 200);
    } else {
      // Pas d'étape sauvegardée = ouverture depuis dialog, signaler qu'il faut le réafficher
      console.log("[useOnboarding] Pas d'étape sauvegardée, signal pour réafficher dialog");
      shouldShowOnboardingDialog.value = true;
    }
  }

  const instance = {
    startTour,
    resetOnboarding: resetOnboardingAPI,
    getProgress,
    chapterOrder: chapterOrder.value,
    markChapterAsComplete,
    isOnboardingActive,
    nextStepIfCurrent,
    showChapterNavigation,
    hideChapterNavigation,
    handleChapterNavigationClose,
    navigateToChapter,
    returnToChapterTransition,
    getCurrentChapter,
    handleOnboardingError,
    skipToNextChapter,
    fetchOnboardingStatus,
    completeOnboardingAPI,
    dismissOnboardingAPI,
    isChapterAvailableForRole,
    getDisplayableChaptersForRole,
    currentRole: currentRole.value,
    isChapterNavigationVisible,
    shouldShowOnboardingDialog,
  };

  // Initialiser la fonction globale pour navigation depuis Shepherd HTML
  if (typeof window !== 'undefined') {
    window.navigateToOnboardingChapter = async (chapterKey: string) => {
      // Fermer tous les tours Shepherd actifs
      const shepherdElements = document.querySelectorAll('.shepherd-element');
      shepherdElements.forEach((el) => el.remove());

      const overlays = document.querySelectorAll('.shepherd-modal-overlay-container');
      overlays.forEach((el) => el.remove());

      // Nettoyer tous les overlays PrimeVue aussi au cas où
      document.querySelectorAll('.p-component-overlay').forEach((el) => el.remove());

      // Attendre un peu pour que les éléments soient supprimés
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Utiliser l'instance globale si elle existe
      if (globalOnboardingInstance) {
        await globalOnboardingInstance.navigateToChapter(chapterKey);
      } else {
        console.error('Global onboarding instance not available');
      }
    };
  }

  // Sauvegarder l'instance globalement pour la navigation
  globalOnboardingInstance = instance;

  return instance;
}

// Fonction globale pour la navigation depuis le HTML Shepherd
declare global {
  interface Window {
    navigateToOnboardingChapter?: (chapterKey: string) => Promise<void>;
  }
}
